/**
 * Server-side counting for OCS list results — the arithmetic the model is not
 * asked to do.
 *
 * WHY THIS EXISTS
 * ---------------
 * `list_package_templates` returns all 55 templates, untruncated and verified
 * against live OCS. Asked "how many are there?", Haiku answered 42; asked how
 * many of those are recurring, it answered 18. Live truth is 55 and 36.
 * Neither wrong number is a subset of anything — it is a model tallying a
 * 55-object JSON dump by eye, which is not a job a language model does
 * reliably and not a job it should be given when the server is holding the
 * array.
 *
 * So the server counts. Every list tool returning an OCS envelope emits a
 * `summary` block ahead of its rows: the row total, plus a tally of the few
 * low-cardinality fields a caller actually aggregates on. The model reads a
 * number instead of deriving one.
 *
 * WHY NOT A COUNT TOOL
 * --------------------
 * A `count_package_templates` tool would fix exactly these two questions, add
 * a 123rd entry to a catalogue whose size already costs tool-selection
 * accuracy, and only ever answer the counting question someone thought to
 * build. It would also be a second code path to the same OCS data, which is a
 * second chance to get tenant scoping wrong. Attaching the count to the read
 * that already happened costs no tool, no extra OCS call, and inherits that
 * read's scoping exactly.
 *
 * TENANCY
 * -------
 * `ocsListSummary` is pure: it counts the rows handed to it and fetches
 * nothing. Call it on the payload AFTER `scopedOcsCall` has filtered it and
 * the summary describes precisely what the caller is allowed to see. Counting
 * before scoping would leak fleet size across tenants — there is no code path
 * in this module that can reach OCS to do that.
 */

import { ocsArray, OCS_LIST_KEYS, type OcsListKind } from "./ocs-array.js";

/** Distinct values a facet may have before only its cardinality is reported. */
export const FACET_TALLY_MAX = 25;

export interface OcsFacetSpec {
  /** Key this facet appears under in `summary.breakdown`. */
  readonly label: string;
  /** Dotted paths into a row, tried in order; the first defined value wins. */
  readonly paths: readonly string[];
}

/**
 * Facets per list kind. Deliberately short: a facet earns its place when a
 * human would plausibly ask "how many X are Y", and only for fields whose
 * value set is small enough to print.
 *
 * The template paths are the OCS field names as captured off the live fleet
 * (`tests/fixtures/response-shapes/list_package_templates.json`) — `recurring`
 * is a root boolean, and the zone name lives nested under `rdbLocationZones`,
 * not at the root beside `locationzoneid`.
 */
export const OCS_LIST_FACETS: Record<OcsListKind, readonly OcsFacetSpec[]> = {
  templates: [
    { label: "recurring", paths: ["recurring"] },
    {
      label: "locationZone",
      paths: [
        "rdbLocationZones.locationzonename",
        "locationzonename",
        "locationzoneid",
      ],
    },
  ],
  subscribers: [{ label: "status", paths: ["status"] }],
  packages: [
    { label: "status", paths: ["status"] },
    { label: "recurring", paths: ["recurring"] },
  ],
  zones: [],
  resellers: [],
};

/**
 * Child collections to count inside each row.
 *
 * `listResellerAccount` nests the accounts under each reseller, so a bare row
 * count answers "how many resellers", which is 1, when every real question
 * about that tool is about the 2 accounts underneath. Shipping `total: 1` with
 * "use these figures directly" in front of that payload is a worse lie than
 * saying nothing: it was measured breaking two eval questions that had been
 * passing. Counting the nesting is the fix — this is the documented trap of
 * that tool, so the summary is the right place to disarm it.
 */
export const OCS_LIST_NESTED: Partial<Record<OcsListKind, { label: string; path: string }>> = {
  resellers: { label: "accounts", path: "account" },
};

export interface OcsFacetSummary {
  /** Distinct values seen across the rows. */
  distinct: number;
  /** Rows per value — omitted when `distinct` exceeds FACET_TALLY_MAX. */
  counts?: Record<string, number>;
  /** Rows where the field was absent or null. Omitted when zero. */
  missing?: number;
}

export interface OcsListSummary {
  /**
   * Rows in the envelope, counted by the server. Authoritative for what is in
   * this result: use it, do not recount the rows below. When `moreAvailable`
   * is true it is the size of this page, not of the fleet.
   */
  total: number;
  /**
   * True when OCS says it holds rows this result does not carry — `hasMore`,
   * or an `nbFound` larger than the rows returned. Present only when true.
   *
   * Without this the note would tell a paged `list_subscribers` result that it
   * is complete, which is exactly the kind of confidently wrong number this
   * whole change exists to stop producing.
   */
  moreAvailable?: true;
  /** OCS `nbFound`: rows matching upstream, before paging. Present when OCS reports it. */
  upstreamTotal?: number;
  /**
   * Rows inside the rows, where the envelope nests — `{ accounts: 2 }` for
   * listResellerAccount. When this is present it is usually the number the
   * question is actually about, and `total` is not.
   */
  nested?: Record<string, number>;
  breakdown?: Record<string, OcsFacetSummary>;
  note: string;
}

/** OCS paging hints, when the envelope carries them (listSubscriber does). */
function pagingHints(data: unknown): { hasMore: boolean; nbFound: number | undefined } {
  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    return { hasMore: false, nbFound: undefined };
  }
  const obj = data as Record<string, unknown>;
  return {
    hasMore: obj.hasMore === true,
    nbFound: typeof obj.nbFound === "number" ? obj.nbFound : undefined,
  };
}

function readPath(row: Record<string, unknown>, path: string): unknown {
  let cur: unknown = row;
  for (const seg of path.split(".")) {
    if (cur === null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/** Facet key for a raw value, or null when the value is not summarisable. */
function facetKey(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "boolean" || typeof v === "number") return String(v);
  if (typeof v === "string") return v;
  return null;
}

/**
 * Count an OCS list envelope: row total plus a tally of the declared facets.
 *
 * Pure — it counts what it is handed. See the tenancy note above.
 */
export function ocsListSummary(
  data: unknown,
  kind: OcsListKind,
  facets: readonly OcsFacetSpec[] = OCS_LIST_FACETS[kind],
): OcsListSummary {
  const rows = ocsArray<Record<string, unknown>>(data, kind);

  const breakdown: Record<string, OcsFacetSummary> = {};
  for (const facet of facets) {
    const counts = new Map<string, number>();
    let missing = 0;

    for (const row of rows) {
      if (row === null || typeof row !== "object") {
        missing += 1;
        continue;
      }
      let key: string | null = null;
      for (const path of facet.paths) {
        key = facetKey(readPath(row, path));
        if (key !== null) break;
      }
      if (key === null) {
        missing += 1;
        continue;
      }
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    if (counts.size === 0 && missing === 0) continue;

    const entry: OcsFacetSummary = { distinct: counts.size };
    if (counts.size > 0 && counts.size <= FACET_TALLY_MAX) {
      // Sorted, so the same fleet renders the same block every call and
      // "first alphabetically" is answerable by reading rather than re-sorting.
      entry.counts = Object.fromEntries(
        [...counts].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
      );
    }
    if (missing > 0) entry.missing = missing;
    breakdown[facet.label] = entry;
  }

  const { hasMore, nbFound } = pagingHints(data);
  const more = hasMore || (nbFound !== undefined && nbFound > rows.length);

  const summary: OcsListSummary = {
    total: rows.length,
    note:
      `Counted server-side: ${rows.length} row${rows.length === 1 ? "" : "s"} in this result. ` +
      "Use these figures directly rather than recounting the rows below. " +
      (more
        ? "This is ONE PAGE — OCS holds more rows than are returned here" +
          (nbFound !== undefined ? ` (${nbFound} match upstream)` : "") +
          ", so do not report `total` as a fleet-wide figure; narrow the filters or page for the rest."
        : "Nothing was truncated: these are all the rows OCS returned."),
  };

  if (more) {
    summary.moreAvailable = true;
    if (nbFound !== undefined) summary.upstreamTotal = nbFound;
  }

  const nest = OCS_LIST_NESTED[kind];
  if (nest) {
    const n = rows.reduce((acc, row) => {
      const child = row && typeof row === "object" ? readPath(row, nest.path) : undefined;
      return acc + (Array.isArray(child) ? child.length : 0);
    }, 0);
    summary.nested = { [nest.label]: n };
    summary.note +=
      ` This envelope NESTS: \`total\` counts the outer rows, while there are ${n} ` +
      `${nest.label} inside them under \`${nest.path}\`. A question about ${nest.label} ` +
      `is answered by that figure, not by \`total\`.`;
  }

  if (Object.keys(breakdown).length > 0) {
    summary.breakdown = breakdown;
    summary.note +=
      " `breakdown` gives, per field, how many distinct values occur and how many rows carry each " +
      "(`missing` counts rows where the field is absent).";
  }

  return summary;
}

/**
 * The OCS payload with its `summary` in front of it.
 *
 * Additive and summary-first: the envelope and every row are untouched, and a
 * model reading the result meets the count before the rows rather than after.
 */
export function withOcsListSummary<T>(
  data: T,
  kind: OcsListKind,
  facets?: readonly OcsFacetSpec[],
): T & { summary: OcsListSummary } {
  const summary = ocsListSummary(data, kind, facets);

  if (data !== null && typeof data === "object" && !Array.isArray(data)) {
    return { summary, ...(data as object) } as T & { summary: OcsListSummary };
  }

  // A bare array (recorded fixtures, older OCS) has nowhere to hang a key.
  // Give it the envelope it should have had rather than dropping the count.
  return { summary, [OCS_LIST_KEYS[kind][0]]: data } as unknown as T & {
    summary: OcsListSummary;
  };
}
